// Log Review Time — the rules behind the "Log Review Time" field on a
// Draughting Sub-task (client, 2026-10-20). No React here: pure, tested by
// customs/test/run-log-review-time-tests.mjs.
//
// The reviewer of a draughting sub-task logs the hours the review took. A
// click on "Log Time" creates a Draughting Sub-task beside the one reviewed
// (same parent, same project) named "REVIEW TIME LOG: <its summary>", whose
// Assigned Resource is the reviewer — the Assigned Resource OPTION that
// matches the Requested Approver by name — carrying the hours as its
// estimate and its actual, and moves it to Done. The time then sits under
// the reviewer like any other finished work, once.
//
// A Requested Approver with no Assigned Resource option of the same name
// cannot be logged to: the field says so, and an admin adds or renames the
// option (Settings → Fields → Assigned Resource).

// Field NAMES, resolved against /api/fields (any case, any punctuation).
export const LOG_REVIEW = {
  approversField: 'Requested Approvers',
  assignedField: 'Assigned Resource',
  estField: 'Estimated Time (Hours)',
  actField: 'Actual Time (Hours)',
  startField: 'Start date',
  completedField: 'Actual Completion Date',
  summaryPrefix: 'REVIEW TIME LOG: ',
  doneStatus: 'Done',
  // Carried from the work item reviewed (as well as whatever its create
  // screen requires): what the Draughting Sub-task workflow's validators ask
  // for on the way to Done (Drawing Task Type, Requested Approvers — BUFFER →
  // QUEUE, IN PROGRESS → REVIEW) and what the reports read it by. Nothing
  // else — a hold-up reason or a flag carried over would be counted twice.
  carry: ['Drawing Task Type', 'Requested Approvers', 'Responsible Engineer:', 'Billable Status', 'Phase', 'Epic Reference'],
  // Set on the log: REVIEW → Done requires the check, and logging the review
  // is the reviewer saying it was done.
  fixed: [{ field: 'Data checked for correctness of: Drawing, QIS, CNC files and RFC Change log.', option: 'Yes' }],
};

export const normName = (s) => String(s || '').toLowerCase().replace(/[^a-z0-9]/g, '');
const pad = (n) => String(n).padStart(2, '0');
export const todayISO = (d = new Date()) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

// The field's id for a configured name (or an id given as such), else null.
export function fieldIdOf(want, fieldList) {
  if (!want) return null;
  if (/^customfield_\d+$/.test(want)) return want;
  const target = normName(want);
  const hit = (fieldList || []).find((f) => f && normName(f.name) === target);
  return hit ? (hit.id || hit.key || null) : null;
}

// How a field is written: a multi-select ([{value}]), one option ({value}),
// a user ({accountId}) or several users ([{accountId}]).
export function kindOf(field) {
  const s = (field && field.schema) || {};
  const t = String(s.type || field?.schemaType || '').toLowerCase();
  if (t === 'user') return 'user';
  if (t === 'array') return String(s.items || '').toLowerCase() === 'user' ? 'users' : 'multi';
  return 'select';
}

// Every person a field value names: users (display name + account id),
// options (their value), names as text, or a list of those. A multi-select
// once saved as JSON text reads as the names inside it.
export function peopleIn(v) {
  let x = v;
  if (typeof x === 'string' && /^\s*[[{]/.test(x)) { try { x = JSON.parse(x); } catch { /* plain text */ } }
  if (x == null || x === '') return [];
  return (Array.isArray(x) ? x : [x]).map((p) => {
    if (p && typeof p === 'object') return { name: String(p.displayName || p.value || p.name || p.emailAddress || '').trim(), accountId: p.accountId || null };
    return { name: String(p == null ? '' : p).trim(), accountId: null };
  }).filter((p) => p.name);
}

// Each Requested Approver against the Assigned Resource's options: its
// option (same name, any case or punctuation), or null — a MISMATCH the
// admin has to fix. A user-type Assigned Resource takes the approver as the
// user they are (no option to match).
export function matchApprovers(approvers, options, assignedKind) {
  const byName = new Map((options || []).map((o) => [normName(typeof o === 'string' ? o : (o.value || o.name || o.label)), typeof o === 'string' ? o : (o.value || o.name || o.label)]));
  return (approvers || []).map((a) => {
    if (assignedKind === 'user' || assignedKind === 'users') return { name: a.name, accountId: a.accountId, option: a.accountId ? a.name : null };
    return { name: a.name, accountId: a.accountId, option: byName.get(normName(a.name)) || null };
  });
}

// Whose review is being logged, by default: the person signed in when they
// are one of the approvers (and can be logged to), else the first approver
// who can. -1 = nobody can.
export function defaultLogger(matches, me) {
  const mine = (matches || []).findIndex((m) => m.option && me && ((me.accountId && m.accountId === me.accountId) || normName(m.name) === normName(me.displayName)));
  if (mine >= 0) return mine;
  return (matches || []).findIndex((m) => m.option);
}

// The Assigned Resource as the create / update writes it.
export function assignedWrite(match, assignedKind) {
  if (!match || !match.option) return null;
  if (assignedKind === 'multi') return [{ value: match.option }];
  if (assignedKind === 'select') return { value: match.option };
  if (!match.accountId) return null;
  return assignedKind === 'users' ? [{ accountId: match.accountId }] : { accountId: match.accountId };
}

// A value as READ (GET /api/issue) → as WRITTEN (POST /api/issue): options
// and users by id, a cascade with its child, a work item by key; text,
// numbers, dates and documents as they are. undefined = leave it out.
export function toWrite(v) {
  if (v == null || v === '') return undefined;
  if (Array.isArray(v)) { const out = v.map(toWrite).filter((x) => x !== undefined); return out.length ? out : undefined; }
  if (typeof v !== 'object') return v;
  if (v.type === 'doc') return v;
  if (v.accountId) return { accountId: v.accountId };
  if (v.key && !v.id && !v.value) return { key: v.key };
  if (v.id != null) { const o = { id: String(v.id) }; if (v.child) { const c = toWrite(v.child); if (c) o.child = c; } return o; }
  if (v.value != null) return { value: v.value };
  if (v.name != null) return { name: v.name };
  return undefined;
}

// The fields the new log is created with. `meta` = the create screen of
// the work type in the project (GET /api/createmeta → { fieldId: { required,
// defaultValue, … } }). Carried from the work item reviewed: the fields the
// create screen REQUIRES and the ones named in LOG_REVIEW.carry (ids.carry)
// — only those, so nothing it is counted by elsewhere (a hold-up reason, a
// flag) is doubled. Set here: project, work type, parent, summary, the
// reviewer as Assigned Resource, the hours as estimate and actual, today as
// due / start / completion date, and LOG_REVIEW.fixed (ids.fixed: [{ id,
// option }]). A field the create screen does not carry goes in `later` —
// written by an update before the log is moved to Done.
export function buildLog({ source, meta, ids, match, assignedKind, hours, today, prefix = LOG_REVIEW.summaryPrefix }) {
  const sf = (source && source.fields) || {};
  const m = meta || {};
  const fields = {
    project: { key: sf.project?.key || String(source?.key || '').split('-')[0] },
    issuetype: sf.issuetype?.id ? { id: String(sf.issuetype.id) } : { name: sf.issuetype?.name },
    summary: `${prefix}${sf.summary || source?.key || ''}`.slice(0, 255),
  };
  if (sf.parent?.key) fields.parent = { key: sf.parent.key };
  const set = {};
  if (ids.assigned) set[ids.assigned] = assignedWrite(match, assignedKind);
  if (ids.est) set[ids.est] = hours;
  if (ids.act) set[ids.act] = hours;
  set.duedate = today;
  if (ids.start) set[ids.start] = today;
  if (ids.completed) set[ids.completed] = today;
  (ids.fixed || []).forEach((x) => { if (x && x.id && x.option) set[x.id] = { value: x.option }; });
  const own = new Set(['project', 'issuetype', 'summary', 'parent', 'description', 'attachment', 'issuelinks', 'reporter', 'creator', 'status', 'resolution', ...Object.keys(set), ...(ids.skip || [])]);
  // the create screen's required fields, from the work item reviewed (else the field's default)
  Object.entries(m).forEach(([fid, f]) => {
    if (!f || !f.required || own.has(fid)) return;
    const w = toWrite(sf[fid]);
    if (w !== undefined) fields[fid] = w;
    else if (f.defaultValue != null) fields[fid] = toWrite(f.defaultValue);
  });
  // the fields the workflow and the reports need, from the work item reviewed
  (ids.carry || []).forEach((fid) => {
    if (!fid || own.has(fid) || fid in fields) return;
    const w = toWrite(sf[fid]);
    if (w !== undefined) set[fid] = w;
  });
  const later = {};
  Object.entries(set).forEach(([fid, v]) => { if (v == null) return; if (fid in m) fields[fid] = v; else later[fid] = v; });
  return { fields, later };
}

// The shortest way from `fromName` to the status named `toName` through a
// workflow's transitions ([{ id, name, from: {name}|null, to: {name}, type }],
// GET /api/jira/issue-workflow). A transition from nowhere (global) is open
// from every status; the initial one never is. [] = already there; null = no way.
export function pathTo(transitions, fromName, toName) {
  const want = normName(toName); const start = normName(fromName);
  if (start === want) return [];
  const list = (transitions || []).filter((t) => t && t.to && String(t.type || '').toLowerCase() !== 'initial');
  const prev = new Map([[start, null]]); const queue = [start];
  while (queue.length) {
    const at = queue.shift();
    for (const t of list) {
      if (t.from && normName(t.from.name) !== at) continue;
      const nx = normName(t.to.name);
      if (prev.has(nx)) continue;
      prev.set(nx, { at, t });
      if (nx === want) {
        const steps = []; let k = nx;
        while (prev.get(k)) { const p = prev.get(k); steps.unshift({ id: String(p.t.id), name: p.t.name, to: p.t.to.name }); k = p.at; }
        return steps;
      }
      queue.push(nx);
    }
  }
  return null;
}

// The status a new work item starts in: where the workflow's initial
// transition (Create) leads — so the log is not read back after its create.
// null when the workflow does not say.
export function initialStatusOf(transitions) {
  const t = (transitions || []).find((x) => x && x.to && String(x.type || '').toLowerCase() === 'initial');
  return t ? t.to.name : null;
}

// The step's transition among those open on the work item now: the planned
// one by id, else any that lands on the same status. One the server lists but
// greys out (a condition — a group the person is not in) is not open.
export function openStep(available, step) {
  const list = (Array.isArray(available) ? available : (available?.transitions || [])).filter((t) => t && t.isAvailable !== false);
  return list.find((t) => String(t.id) === String(step.id)) || list.find((t) => normName(t.to?.name || t.to) === normName(step.to)) || null;
}

// The logs already made for this work item: its siblings named
// "<prefix><its summary>", newest first, with whom they were logged to and
// their hours.
export function logsOf(siblings, source, ids, prefix = LOG_REVIEW.summaryPrefix) {
  const want = normName(`${prefix}${source?.fields?.summary || ''}`);
  return (siblings || []).filter((s) => s && s.key !== source?.key && normName(s.fields?.summary) === want)
    .map((s) => ({
      key: s.key,
      who: peopleIn(ids.assigned ? s.fields?.[ids.assigned] : null).map((p) => p.name).join(', '),
      hours: Number(ids.act ? s.fields?.[ids.act] : NaN),
      status: s.fields?.status?.name || '',
      created: String(s.fields?.created || '').slice(0, 10),
    }))
    .sort((a, b) => b.created.localeCompare(a.created) || b.key.localeCompare(a.key, undefined, { numeric: true }));
}

// Hours as typed: a positive number up to a working week, quarter hours.
export function parseHours(text) {
  const n = Number(String(text || '').replace(',', '.').trim());
  if (!Number.isFinite(n) || n <= 0 || n > 60) return null;
  return Math.round(n * 100) / 100;
}
